/**
 * §P.1 · Alta y edición de un alumno.
 *
 * Un solo formulario para las dos cosas, porque los campos son los mismos y
 * tenerlos en dos sitios es cómo uno acaba pidiendo la fecha de alta y el otro no.
 *
 * Lo que este fichero resuelve y no es evidente:
 *
 * **La precondición no es opcional.** `PATCH` sin `If-Match` responde `428`, así
 * que el `ETag` del `GET` hay que guardarlo y mandarlo. Y el `PATCH` devuelve uno
 * nuevo: se recoge, y así se puede guardar dos veces seguidas sin recargar.
 *
 * **Un `412` no se enseña en rojo.** Es «el dueño acaba de cambiar esto desde su
 * móvil»: se recarga, **se dice qué cambió** y se deja reintentar. Ver §3.8.
 *
 * **Con el club suspendido (`423`) el formulario es de lectura.** No se apaga la
 * aplicación —un club que se retrasa un mes no puede dejar a 120 familias sin el
 * historial de sus hijos— pero tampoco se ofrece un botón que va a fallar.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  camposQueCambiaron,
  esConflictoDeEdicion,
  etagDe,
  faltaLaPrecondicion,
} from '@/shared/api/concurrencia';
import {
  aProblema,
  ErrorDeApi,
  esClubSuspendido,
  type Problema,
} from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import type { Alumno, EstadoAlumno } from '@/club/alumnos/tipos';

/** Lo que se teclea. Todo texto: un formulario no tiene tipos, tiene cadenas. */
interface Campos {
  first_name: string;
  last_name: string;
  code: string;
  birthdate: string;
  gender: string;
  status: EstadoAlumno;
  joined_on: string;
}

const ETIQUETAS: Record<string, string> = {
  first_name: 'el nombre',
  last_name: 'el apellido',
  code: 'el número de socio',
  birthdate: 'la fecha de nacimiento',
  gender: 'el sexo',
  status: 'el estado',
  joined_on: 'la fecha de alta',
  left_on: 'la fecha de baja',
  leave_reason: 'el motivo de la baja',
};

/** `Date` → `yyyy-mm-dd` para un `<input type="date">`, **en local**. */
const paraInput = (valor: Date | null | undefined): string => {
  if (!valor) return '';
  const d = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  // No `toISOString()`: recortarlo saca el día anterior al oeste de Greenwich.
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

const hoy = () => paraInput(new Date());

const desdeAlumno = (a: Alumno | null): Campos => ({
  first_name: a?.first_name ?? '',
  last_name: a?.last_name ?? '',
  code: a?.code ?? '',
  birthdate: paraInput(a?.birthdate ?? null),
  gender: '',
  status: (a?.status as EstadoAlumno) ?? 'active',
  joined_on: a ? paraInput(a.joined_on) : hoy(),
});

/** Vacío es `null`, no `''`: la columna es nullable y `''` sería un dato falso. */
const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function FormularioDeAlumno({
  club,
  alumno,
  etag,
  suspendido,
  alGuardar,
  alCancelar,
}: {
  club: string;
  /** `null` para un alta. */
  alumno: Alumno | null;
  /** El `ETag` del `GET`. Obligatorio para editar. */
  etag: string | null;
  suspendido: boolean;
  alGuardar: (alumnoId: string) => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const editando = alumno !== null;

  const [campos, setCampos] = useState<Campos>(() => desdeAlumno(alumno));
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conflicto, setConflicto] = useState<readonly string[] | null>(null);
  /*
   * El `ETag` vivo: nace del `GET` y lo renueva cada `PATCH` que sale bien.
   *
   * El ajuste va **durante el pintado** y no en un efecto: un `setState` dentro
   * de un `useEffect` provoca un segundo pintado en cascada, y aquí el valor se
   * deriva de una propiedad, así que no hace falta esperar a nada. Es el patrón
   * que React documenta para «ajustar estado cuando cambia una propiedad».
   */
  const [etagRecibido, setEtagRecibido] = useState(etag);
  const [etagVivo, setEtagVivo] = useState(etag);

  if (etag !== etagRecibido) {
    setEtagRecibido(etag);
    setEtagVivo(etag);
  }

  const cambiar = (campo: keyof Campos, valor: string) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    setTocados((t) => new Set(t).add(campo));
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const cuerpo = {
        first_name: campos.first_name.trim(),
        last_name: oNulo(campos.last_name),
        code: oNulo(campos.code),
        birthdate: oNulo(campos.birthdate),
        status: campos.status,
      };

      if (!editando) {
        const r = await api.v1.clubs({ club }).students.post({
          ...cuerpo,
          joined_on: campos.joined_on,
        });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return { alumno: r.data as unknown as Alumno, etag: etagDe(r) };
      }

      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumno.id })
        .patch(cuerpo, {
          // Sin esto es un 428. La precondición es parte de la operación, no un
          // extra opcional.
          headers: etagVivo ? { 'if-match': etagVivo } : {},
        });

      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { alumno: r.data as unknown as Alumno, etag: etagDe(r) };
    },

    onSuccess: ({ alumno: guardado, etag: nuevo }) => {
      setProblema(null);
      setConflicto(null);
      setTocados(new Set());
      // El `ETag` que vino con la respuesta: permite guardar otra vez sin
      // recargar la ficha.
      if (nuevo) setEtagVivo(nuevo);

      void cache.invalidateQueries({ queryKey: ['alumnos', club] });
      void cache.invalidateQueries({ queryKey: ['alumno', club, guardado.id] });

      alGuardar(guardado.id);
    },

    onError: async (error) => {
      const p = aProblema(error);

      if (!esConflictoDeEdicion(p) || !editando) {
        setProblema(p);
        return;
      }

      // §3.8: recargar, enseñar qué cambió y dejar reintentar. Lo que se compara
      // es la ficha con la que se abrió el formulario contra la de ahora, sin
      // contar los campos que yo he tocado.
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumno.id })
        .get();

      if (r.error || !r.data) {
        setProblema(p);
        return;
      }

      const actual = r.data as unknown as Alumno;
      setEtagVivo(etagDe(r));
      setConflicto(
        camposQueCambiaron(
          alumno as unknown as Record<string, unknown>,
          actual as unknown as Record<string, unknown>,
          [...tocados]
        )
      );
    },
  });

  if (suspendido) {
    return (
      <div className="hueco">
        <h2>La cuenta del club está suspendida</h2>
        <p>
          Puedes consultar todo, pero no guardar cambios hasta que se resuelva
          el impago. Escríbenos a{' '}
          <a href="mailto:hola@kiapclubs.com">hola@kiapclubs.com</a>.
        </p>
        <div className="hueco-accion">
          <button type="button" className="btn-plano" onClick={alCancelar}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <h1>{editando ? 'Editar alumno' : 'Nuevo alumno'}</h1>

      {conflicto && <AvisoDeConflicto campos={conflicto} />}

      <div className="rejilla">
        <label>
          Nombre
          <input
            required
            maxLength={80}
            value={campos.first_name}
            onChange={(e) => cambiar('first_name', e.target.value)}
          />
        </label>

        <label>
          Apellidos
          <input
            maxLength={120}
            value={campos.last_name}
            onChange={(e) => cambiar('last_name', e.target.value)}
          />
        </label>

        <label>
          Número de socio
          <input
            maxLength={40}
            placeholder="Opcional"
            value={campos.code}
            onChange={(e) => cambiar('code', e.target.value)}
          />
        </label>

        <label>
          Fecha de nacimiento
          <input
            type="date"
            value={campos.birthdate}
            onChange={(e) => cambiar('birthdate', e.target.value)}
          />
        </label>

        <label>
          Estado
          <select
            value={campos.status}
            onChange={(e) => cambiar('status', e.target.value)}
          >
            <option value="active">Activo</option>
            <option value="trial">En prueba</option>
            <option value="paused">En pausa</option>
            <option value="left">De baja</option>
          </select>
        </label>

        {!editando && (
          <label>
            Fecha de alta
            <input
              type="date"
              required
              value={campos.joined_on}
              onChange={(e) => cambiar('joined_on', e.target.value)}
            />
          </label>
        )}
      </div>

      {problema && (
        <PanelError
          problema={problema}
          {...(esClubSuspendido(problema)
            ? {}
            : { reintentar: () => guardar.mutate() })}
        />
      )}

      <div className="acciones">
        <button type="submit" disabled={guardar.isPending}>
          {guardar.isPending
            ? 'Guardando…'
            : editando
              ? 'Guardar cambios'
              : 'Dar de alta'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={alCancelar}
          disabled={guardar.isPending}
        >
          Cancelar
        </button>
      </div>

      {editando &&
        faltaLaPrecondicion(problema ?? { title: '', status: 0 }) && (
          // Si esto sale, el fallo es nuestro: se perdió el `ETag` por el camino.
          <p className="traza">
            Falta la precondición de edición. Vuelve a abrir la ficha.
          </p>
        )}
    </form>
  );
}

/**
 * El `412`, dicho como lo que es.
 *
 * Ni rojo, ni «error de validación»: alguien más estaba editando lo mismo. Lo
 * único que hace falta saber es **qué tocó**, para decidir si guardar encima o
 * cancelar.
 */
function AvisoDeConflicto({ campos }: { campos: readonly string[] }) {
  const lista = campos.map((c) => ETIQUETAS[c] ?? c);

  return (
    <div className="aviso" role="status">
      <strong>Alguien ha cambiado esta ficha mientras la editabas.</strong>{' '}
      {lista.length > 0 ? (
        <>
          Cambió {lista.join(', ')}. Lo tuyo sigue escrito abajo: si le das a
          guardar otra vez, se queda tu versión.
        </>
      ) : (
        <>
          Los cambios no afectan a lo que estás tocando, así que puedes guardar
          otra vez sin perder nada.
        </>
      )}
    </div>
  );
}
